import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';

export type Plan = 'free' | 'growth' | 'global' | 'enterprise';

export const PLAN_RANK: Record<Plan, number> = {
  free: 0,
  growth: 1,
  global: 2,
  enterprise: 3,
};

/**
 * Monthly AI cap (in USD) by plan, mirroring the backend tier table. -1 means
 * unlimited. Frontend keeps a local copy so we don't need a round-trip just
 * to render the progress bar; the backend webhook is the source of truth.
 */
export const PLAN_AI_CAP_USD: Record<Plan, number> = {
  free: 0,
  growth: 2000,
  global: 4000,
  enterprise: -1,
};

/**
 * Returns true when `actual` satisfies `required`. A missing `actual` is
 * treated as 'free' so loading states don't briefly flash gated content.
 */
export function meetsPlan(actual: Plan | undefined, required: Plan): boolean {
  const a = actual ?? 'free';
  return PLAN_RANK[a] >= PLAN_RANK[required];
}

interface PublicCompany {
  id: string;
  plan: Plan;
}

/**
 * Resolves the current user's company plan. Defaults to `free` while the
 * query is pending so we err on the side of *hiding* features the user
 * hasn't paid for. Cached per-session via react-query.
 */
export function usePlan(): {
  plan: Plan;
  companyId: string | undefined;
  meets: (req: Plan) => boolean;
  isLoading: boolean;
} {
  const q = useQuery<PublicCompany[]>({
    queryKey: ['companies', 'mine'],
    queryFn: async () => (await api.get<PublicCompany[]>('/api/companies')).data,
  });

  const company = q.data?.[0];
  const plan: Plan = company?.plan ?? 'free';

  return {
    plan,
    companyId: company?.id,
    meets: (req: Plan) => meetsPlan(plan, req),
    isLoading: q.isLoading,
  };
}
